/**
 * Available Backend Brand Identifiers:
 * - "LifeEssentialsClub" (support@lifeessentials.club)
 * - "TomMillerServices" (contact@tommillerservices.com)
 * - "ByronBayToken" (support@byronbaytoken.com)
 * - "TheConnectionCode" (support@theconnectioncode.com)
 * 
 * Each brand has its own email templates, logos, and sender configurations
 * on the backend. The brandIdentifier must match exactly.
 */

export interface BrandConfig {
  // Core Brand Identity
  siteName: string;
  tagline: string;
  description: string;
  
  // Technical Configuration
  tenantId: string;
  apiUrl?: string; // Optional override for API URL
  brandIdentifier: string; // Backend brand identifier for email templates and notifications
  
  // Logo and Images
  logo: {
    src: string;
    alt: string;
    favicon: string;
  };
  
  // Color Palette
  colors: {
    // Primary brand colors
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;
    
    // Accent colors
    accent: string;
    accentLight: string;
    
    // Background colors
    background: {
      main: string;
      light: string;
      card: string;
      overlay: string;
    };
    
    // Status colors
    status: {
      success: string;
      warning: string;
      error: string;
      info: string;
    };
    
    // Text colors
    text: {
      primary: string;
      secondary: string;
      muted: string;
      inverse: string;
    };
    
    // Border colors
    border: {
      light: string;
      medium: string;
      dark: string;
    };
  };
  
  // Typography
  fonts: {
    heading: string;
    body: string;
  };
  
  // Component styling
  components: {
    button: {
      primaryBg: string;
      primaryHover: string;
      secondaryBg: string;
      secondaryHover: string;
    };
    card: {
      bg: string;
      border: string;
      shadow: string;
    };
    navbar: {
      bg: string;
      text: string;
    };
  };
  
  // Business specific
  membership: {
    totalLegacyMemberships: number;
    memberBenefits: {
      discount: string;
      features: string[];
    };
  };
}

// Multipolar Peace Configuration
export const brandConfig: BrandConfig = {
  // Core Brand Identity
  siteName: "Multipolar Peace",
  tagline: "A New Era of Global Harmony",
  description: "At Multipolar Peace, we embrace the richness of a multipolar world committed to harmony and understanding. We believe in the power of collective action and shared insights to foster global peace.",
  
  // Technical Configuration
  tenantId: "6844f6ff23d750abb5220219", // TODO: Update with Multipolar Peace tenant ID
  brandIdentifier: "MultiPolarPeace", // TODO: Update when backend config is ready
  
  // Logo and Images
  logo: {
    src: "☮", // Universal peace symbol - Fabricon
    alt: "Multipolar Peace Logo",
    favicon: "%PUBLIC_URL%/favicon.ico"
  },
  
  // Color Palette
  colors: {
    // Primary brand colors (diplomatic navy)
    primary: "#1e3a8a",
    primaryHover: "#1e40af",
    secondary: "#059669",
    secondaryHover: "#047857",
    
    // Accent colors (peaceful green)
    accent: "#059669",
    accentLight: "#10b981",
    
    // Background colors
    background: {
      main: "#f8fafc",
      light: "#f1f5f9",
      card: "#ffffff",
      overlay: "rgba(30, 58, 138, 0.1)"
    },
    
    // Status colors
    status: {
      success: "#059669",
      warning: "#d97706",
      error: "#dc2626",
      info: "#1e3a8a"
    },
    
    // Text colors
    text: {
      primary: "#0f172a",
      secondary: "#475569",
      muted: "#64748b",
      inverse: "#ffffff"
    },
    
    // Border colors
    border: {
      light: "rgba(30, 58, 138, 0.1)",
      medium: "rgba(30, 58, 138, 0.2)",
      dark: "rgba(30, 58, 138, 0.3)"
    }
  },
  
  // Typography
  fonts: {
    heading: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    body: "Inter, -apple-system, BlinkMacSystemFont, sans-serif"
  },
  
  // Component styling
  components: {
    button: {
      primaryBg: "#059669",
      primaryHover: "#1e3a8a",
      secondaryBg: "transparent",
      secondaryHover: "#f1f5f9"
    },
    card: {
      bg: "#ffffff",
      border: "rgba(30, 58, 138, 0.1)",
      shadow: "0 4px 20px rgba(30, 58, 138, 0.08)"
    },
    navbar: {
      bg: "#ffffff",
      text: "#1e3a8a"
    }
  },
  
  // Business specific
  membership: {
    totalLegacyMemberships: 1000, // Community of peace advocates
    memberBenefits: {
      discount: "Free access",
      features: [
        "Expert Insights",
        "Global Dialogue",
        "Peace Advocacy",
        "Collaborative Action"
      ]
    }
  }
};

// Helper functions for easy access
export const getColor = (path: string): string => {
  const keys = path.split(".");
  let value: any = brandConfig.colors;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Color path "${path}" not found in brand config`);
      return "#000000";
    }
  }
  
  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;
  
      for (const key of keys) {
      value = value[key];
      if (value === undefined) {
        console.warn(`Brand config path "${path}" not found`);
        return null;
      }
    }
  
  return value;
};

// CSS Custom Properties Generator
export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;
  
  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};
      
      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      
      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};
      
      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};
      
      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};
    }
  `;
};

export default brandConfig; 